const $getCsrfMetaTag = () => document.querySelector('meta[name="csrf-token"]');
const $getCsrfInputs = () => document.querySelectorAll('input[name="authenticity_token"]');
const getCurrentCsrfToken = () => $getCsrfMetaTag()?.getAttribute("content") || "";

const setCsrfMetaTag = token => {
  $getCsrfMetaTag()?.setAttribute("content", token);
};

const setCsrfInputs = token => {
  $getCsrfInputs().forEach($input => $input.setAttribute("value", token));
};

const notifyCsrfMonitoring = async (message, error, source) => {
  const params = {
    error: error?.message,
    status: error?.status,
    url: window.location.href,
    source
  };
  const eventName = message.toLowerCase().replaceAll(" ", "_");

  // Dynamic imports to avoid circular dependency:
  // request.js → csrf-token-manager → jiffy-analytics → api.js → methods.js → request.js
  const [{ notifyError }, { track }] = await Promise.all([
    import("common/utils/app-monitoring/notify"),
    import("jiffy-analytics")
  ]);
  notifyError(message, params);
  track(eventName, params);
};

const updateCsrfTokens = (token, source) => {
  try {
    setCsrfMetaTag(token);
    setCsrfInputs(token);
    return true;
  } catch (error) {
    notifyCsrfMonitoring("CSRF token update failed", error, source).catch(e => {
      console.warn("CSRF monitoring failed:", e); // eslint-disable-line no-console
    });
    return false;
  }
};

const refreshCsrfToken = async source => {
  try {
    const response = await fetch("/api/refresh_token", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCurrentCsrfToken()
      }
    });

    if (!response.ok) {
      throw new Error("Failed to refresh CSRF token");
    }

    const data = await response.json();
    return updateCsrfTokens(data.csrf_token, source);
  } catch (error) {
    notifyCsrfMonitoring("CSRF token refresh failed", error, source).catch(e => {
      console.warn("CSRF monitoring failed:", e); // eslint-disable-line no-console
    });
    return false;
  }
};

const containsCsrfError = text =>
  typeof text === "string" &&
  (text.includes("InvalidAuthenticityToken") || text.includes("CSRF token authenticity"));

const getStatus = error => error.status ?? error.response?.status;

const isSessionExpired = error => {
  // Check error.message (fetch errors set this to the response body)
  try {
    const parsed = JSON.parse(error.message);
    if (parsed?.error === "Session expired") return true;
  } catch {
    // not JSON — fall through to string checks
  }

  const status = getStatus(error);
  if (status === 422 && containsCsrfError(error.message)) return true;

  // Axios 1.x: response body lives in error.response.data, not error.message
  const axiosData = error.response?.data;
  if (status === 422 && axiosData) {
    try {
      const text = typeof axiosData === "string" ? axiosData : JSON.stringify(axiosData);
      if (containsCsrfError(text)) return true;
      const parsed = typeof axiosData === "string" ? JSON.parse(axiosData) : axiosData;
      if (parsed?.error === "Session expired") return true;
    } catch {
      // not JSON — already checked as string above
    }
  }

  return false;
};

const withCsrfRetry = async (requestFn, source) => {
  try {
    return await requestFn();
  } catch (error) {
    if (!isSessionExpired(error)) throw error;

    const refreshed = await refreshCsrfToken(source);
    if (!refreshed) {
      throw error;
    }

    return requestFn();
  }
};

export { refreshCsrfToken, getCurrentCsrfToken, isSessionExpired, withCsrfRetry };
